/**
 * Annual wastewater activity drivers for inventory-year scaling (wastewater-1.4).
 *
 * public.service_population.service_pop is community service population
 * (residents plus employees). It is not wastewater/sewer service population.
 *
 * Driver preference when scaling from a verified year:
 *   1. wastewater flow (when both years have comparable positive flow)
 *   2. dedicated wastewater/sewer service population (when a source exists)
 *   3. community service population (service_population.service_pop)
 *   4. jurisdiction population
 */

import { supabaseClient } from '../supabase';
import { wastewaterGeofipsVariants } from './wastewaterGeofips';

export type WastewaterActivityDriverKind =
  | 'wastewater_flow'
  | 'wastewater_service_population'
  | 'community_service_population'
  | 'jurisdiction_population';

export interface WastewaterYearActivity {
  year: number;
  /** residents + employees from public.service_population.service_pop */
  communityServicePopulation: number | null;
  /** Dedicated sewer/wastewater service population when a source exists (none in current schema). */
  wastewaterServicePopulation: number | null;
  wastewaterFlow: number | null;
  jurisdictionPopulation: number | null;
}

export interface WastewaterAnnualActivityDrivers {
  geofips: string;
  byYear: Map<number, WastewaterYearActivity>;
  /** Human-readable gaps when a year cannot be scaled. */
  missingDriverNotes: string[];
}

export interface SelectedActivityDriver {
  kind: WastewaterActivityDriverKind;
  value: number;
  year: number;
  sourceTable: string;
  sourceField: string;
}

/** Authoritative preference: never silently prefer community service pop over flow. */
export const DRIVER_PREFERENCE: WastewaterActivityDriverKind[] = [
  'wastewater_flow',
  'wastewater_service_population',
  'community_service_population',
  'jurisdiction_population',
];

export const DRIVER_SOURCE: Record<
  WastewaterActivityDriverKind,
  { table: string; field: string }
> = {
  wastewater_flow: { table: 'wastewater_activity', field: 'treatment_volume_af' },
  wastewater_service_population: {
    table: '(none - no dedicated sewer service population source in current schema)',
    field: 'n/a',
  },
  community_service_population: { table: 'service_population', field: 'service_pop' },
  jurisdiction_population: { table: 'population', field: 'population' },
};

function activityValue(
  row: WastewaterYearActivity | undefined,
  kind: WastewaterActivityDriverKind
): number | null {
  if (!row) return null;
  if (kind === 'wastewater_flow') return row.wastewaterFlow;
  if (kind === 'wastewater_service_population') return row.wastewaterServicePopulation;
  if (kind === 'community_service_population') return row.communityServicePopulation;
  return row.jurisdictionPopulation;
}

/** Choose the best shared driver present for both years with positive values. */
export function selectSharedActivityDriver(
  source: WastewaterYearActivity | undefined,
  target: WastewaterYearActivity | undefined
): { source: SelectedActivityDriver; target: SelectedActivityDriver } | null {
  if (!source || !target) return null;
  for (const kind of DRIVER_PREFERENCE) {
    const s = activityValue(source, kind);
    const t = activityValue(target, kind);
    if (s != null && t != null && s > 0 && t > 0) {
      const meta = DRIVER_SOURCE[kind];
      return {
        source: {
          kind,
          value: s,
          year: source.year,
          sourceTable: meta.table,
          sourceField: meta.field,
        },
        target: {
          kind,
          value: t,
          year: target.year,
          sourceTable: meta.table,
          sourceField: meta.field,
        },
      };
    }
  }
  return null;
}

export function scaleEmissionsByActivityRatio(
  sourceEmissionsMt: number,
  sourceDriver: number,
  targetDriver: number
): number {
  return sourceEmissionsMt * (targetDriver / sourceDriver);
}

export function emptyWastewaterYearActivity(year: number): WastewaterYearActivity {
  return {
    year,
    communityServicePopulation: null,
    wastewaterServicePopulation: null,
    wastewaterFlow: null,
    jurisdictionPopulation: null,
  };
}

export function emptyWastewaterAnnualActivityDrivers(
  geofips: string,
  note?: string
): WastewaterAnnualActivityDrivers {
  return {
    geofips,
    byYear: new Map(),
    missingDriverNotes: note ? [note] : [],
  };
}

/**
 * Fetch annual drivers for inventory scaling.
 * Does not invent values: years without any driver remain absent from byYear.
 */
export async function fetchWastewaterAnnualActivityDrivers(
  geofips: string | number,
  years: number[]
): Promise<WastewaterAnnualActivityDrivers> {
  const yearSet = new Set(years);
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  const variants = wastewaterGeofipsVariants(geofips);
  const byYear = new Map<number, WastewaterYearActivity>();
  const missingDriverNotes: string[] = [];

  const ensureYear = (year: number): WastewaterYearActivity => {
    const existing = byYear.get(year);
    if (existing) return existing;
    const row = emptyWastewaterYearActivity(year);
    byYear.set(year, row);
    return row;
  };

  // Community service population = residents + employees (NOT wastewater service population).
  let loadedCommunityServicePop = false;
  for (const gf of variants) {
    const { data, error } = await supabaseClient
      .from('service_population')
      .select('year, service_pop')
      .eq('geofips', gf)
      .gte('year', minYear)
      .lte('year', maxYear)
      .order('year', { ascending: true });
    if (error || !data?.length) continue;
    for (const row of data) {
      const year = Number(row.year);
      if (!yearSet.has(year) && (year < minYear || year > maxYear)) continue;
      const value = Number(row.service_pop);
      if (!Number.isFinite(value) || value <= 0) continue;
      ensureYear(year).communityServicePopulation = value;
      loadedCommunityServicePop = true;
    }
    if (loadedCommunityServicePop) break;
  }

  // Flow from wastewater_activity when present (sum treatment_volume_af per inventory year).
  // Only usable for scaling when both source and target years have positive flow.
  for (const gf of variants) {
    const { data, error } = await supabaseClient
      .from('wastewater_activity')
      .select('inventory_year, treatment_volume_af')
      .eq('geofips', gf)
      .gte('inventory_year', minYear)
      .lte('inventory_year', maxYear);
    if (error || !data?.length) continue;
    for (const row of data) {
      const year = Number(row.inventory_year);
      const value = Number(row.treatment_volume_af);
      if (!Number.isFinite(value) || value <= 0) continue;
      const target = ensureYear(year);
      target.wastewaterFlow = (target.wastewaterFlow ?? 0) + value;
    }
    break;
  }

  // No dedicated wastewater/sewer service population column exists in the current schema.
  // Leave wastewaterServicePopulation null so community / jurisdiction proxies are used instead.

  // Jurisdiction population fallback (numeric geofips without leading zero).
  const shortGeofips = String(Number(String(geofips).replace(/^0+/, '') || geofips));
  const { data: popData, error: popError } = await supabaseClient
    .from('population')
    .select('year, population')
    .eq('geofips', Number(shortGeofips))
    .gte('year', minYear)
    .lte('year', maxYear)
    .order('year', { ascending: true });

  if (!popError && popData?.length) {
    for (const row of popData) {
      const year = Number(row.year);
      const value = Number(row.population);
      if (!Number.isFinite(value) || value <= 0) continue;
      ensureYear(year).jurisdictionPopulation = value;
    }
  }

  for (const year of years) {
    const row = byYear.get(year);
    const hasDriver =
      row &&
      ((row.wastewaterFlow != null && row.wastewaterFlow > 0) ||
        (row.wastewaterServicePopulation != null && row.wastewaterServicePopulation > 0) ||
        (row.communityServicePopulation != null && row.communityServicePopulation > 0) ||
        (row.jurisdictionPopulation != null && row.jurisdictionPopulation > 0));
    if (!hasDriver) {
      missingDriverNotes.push(
        `No wastewater flow, wastewater service population, community service population, or jurisdiction population for inventory year ${year}.`
      );
    }
  }

  if (byYear.size === 0) {
    missingDriverNotes.push(
      'No annual wastewater activity drivers could be resolved for this geography; modeled years left unavailable.'
    );
  }

  return { geofips: String(geofips), byYear, missingDriverNotes };
}
